/**
 * My Bag — the analysis headline and the finding cards (MY_BAG_PLAN.md §3.4,
 * §3.5).
 *
 * Lifted verbatim out of `MyBagPage` in S8 so the Gap Map can render them:
 * §7.2 routes `/bag?view=map&focus=bends` straight at the findings, which only
 * works if the findings live on the map. The Rack keeps the input job.
 *
 * Three rules here are load-bearing rather than cosmetic:
 *   - the tier is always shown (§3.4) — a tier-3 answer must not be dressed up
 *     as a tier-1 one, and a genuinely new live user arrives at tier 3;
 *   - every bend card prints its `caveat` verbatim (§9.3);
 *   - the order the API returns is the order rendered — `rank_findings` has
 *     already put bends before purchases (§3.5), and re-sorting would undo it.
 */
import React from 'react';

import { roundYards } from './gapMapModel';
import {
  BagAnalysis,
  BendFinding,
  BlockedBend,
  CutFinding,
  Finding,
  HoleFinding,
  OverlapFinding,
  SlotFinding,
  SwapFinding,
} from '../../types/bag.types';

/** §9.1 — say it every time, on the page and again on any swap card. */
export const LONG_IRON_CAVEAT =
  'There is no measured long-iron data anywhere in this system. Every long-iron ' +
  'verdict here is projected from your 7-iron, not measured.';

export const yards = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${roundYards(n)}y`;

export const tierChipClasses = (tier: number): string => {
  if (tier === 1) return 'bg-green/15 text-green-ink';
  if (tier === 2) return 'bg-score-2-bg text-score-2';
  if (tier === 3) return 'bg-cyan-ink/10 text-cyan-ink';
  return 'bg-score-1-bg text-score-1';
};

export const tierExplainer = (tier: number): string => {
  if (tier === 1) return 'Your own launch-monitor numbers are driving this.';
  if (tier === 2) return 'Based on your swing speed and handicap, not on measured launch data.';
  if (tier === 3)
    return 'Based on your distances alone. Add your 7-iron launch-monitor numbers for a sharper answer.';
  return 'Not enough to work from yet — enter a few carries and this fills in.';
};

/**
 * The chip row that has to accompany any advice: how many clubs, what evidence
 * tier produced the answer, and the two constants the arithmetic ran on.
 */
export const AnalysisHeadline: React.FC<{ analysis: BagAnalysis; compact?: boolean }> = ({
  analysis,
  compact = false,
}) => (
  <>
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold px-3 py-1 rounded-brand-sm bg-score-2-bg text-ink">
        {analysis.in_bag_count} / 14 in the bag
      </span>
      <span
        className={`text-sm font-semibold px-3 py-1 rounded-brand-sm ${tierChipClasses(
          analysis.tier
        )}`}
      >
        Tier {analysis.tier} — {analysis.tier_name}
      </span>
      {analysis.target_gap !== null && (
        <span className="text-sm px-3 py-1 rounded-brand-sm bg-score-2-bg text-ink-2">
          Target gap {yards(analysis.target_gap)}
        </span>
      )}
      {analysis.yards_per_degree !== null && (
        <span className="text-sm px-3 py-1 rounded-brand-sm bg-score-2-bg text-ink-2">
          {analysis.yards_per_degree.toFixed(1)} y/° ({analysis.ypd_source ?? 'unknown source'})
        </span>
      )}
    </div>
    {!compact && (
      <>
        <p className="text-sm text-ink-2 mt-2">{tierExplainer(analysis.tier)}</p>
        <p className="text-sm text-ink-3 mt-2">{LONG_IRON_CAVEAT}</p>
        {analysis.notes.length > 0 && (
          <ul className="mt-3 text-sm text-ink-2 list-disc list-inside space-y-1">
            {analysis.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        )}
      </>
    )}
  </>
);

const CardShell: React.FC<{ kicker: string; accent: string; children: React.ReactNode }> = ({
  kicker,
  accent,
  children,
}) => (
  <div className="bg-paper border border-line rounded-brand shadow-raised p-4">
    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${accent}`}>{kicker}</p>
    {children}
  </div>
);

const BendCard: React.FC<{ f: BendFinding }> = ({ f }) => (
  <CardShell kicker="Bend" accent="text-green-ink">
    <p className="text-base text-ink">{f.summary}</p>
    <p className="text-sm text-ink-2 mt-1">
      {f.club_label} · {f.direction} {Math.abs(f.degrees).toFixed(1)}° ·{' '}
      {yards(f.original_carry)} → {yards(f.projected_carry)} · recovers {yards(f.yards_recovered)}
      {f.at_cap && ' · at the bend limit for this head'}
    </p>
    {/* §9.3: verbatim, on every bend card, without exception. */}
    <p className="mt-3 text-sm text-score-1 bg-score-1-bg border border-line rounded-brand-sm px-3 py-2">
      {f.caveat}
    </p>
    {f.guardrail_reason && <p className="mt-2 text-sm text-ink-3">{f.guardrail_reason}</p>}
  </CardShell>
);

const SwapCard: React.FC<{ f: SwapFinding }> = ({ f }) => (
  <CardShell kicker="Swap" accent="text-cyan-ink">
    <p className="text-base text-ink">{f.summary}</p>
    <p className="text-sm text-ink-2 mt-1">
      {f.slot_label} · {f.verdict}
      {f.club_type && ` · ${f.club_type}`}
      {f.club_type_alternative && ` (or ${f.club_type_alternative})`}
      {f.named_wood && ` · ${f.named_wood}`}
      {f.loft_band && ` · ${f.loft_band}`}
    </p>
    <p className="text-sm text-ink-2">
      Closes a {yards(f.hole_gap_yards)} hole · recovers {yards(f.yards_recovered)}
    </p>
    <p className="text-xs text-ink-3 mt-2">
      Evidence tier {f.tier} — {f.tier_name}
    </p>
    {f.reasons.length > 0 && (
      <ul className="mt-2 text-sm text-ink-2 list-disc list-inside space-y-1">
        {f.reasons.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
    )}
    <div className="mt-3 text-sm text-score-1 bg-score-1-bg border border-line rounded-brand-sm px-3 py-2 space-y-1">
      <p>{LONG_IRON_CAVEAT}</p>
      {f.caveats.map((c, i) => (
        <p key={i}>{c}</p>
      ))}
    </div>
  </CardShell>
);

const SlotCard: React.FC<{ f: SlotFinding }> = ({ f }) => (
  <CardShell kicker="Free slot" accent="text-ink-2">
    <p className="text-base text-ink">{f.summary}</p>
    {f.biggest_gap_upper && f.biggest_gap_lower && (
      <p className="text-sm text-ink-2 mt-1">
        Biggest gap: {f.biggest_gap_lower} → {f.biggest_gap_upper} ({yards(f.biggest_gap_yards)})
      </p>
    )}
  </CardShell>
);

const CutCard: React.FC<{ f: CutFinding }> = ({ f }) => (
  <CardShell kicker="Over 14" accent="text-score-1">
    <p className="text-base text-ink">{f.summary}</p>
    {f.candidates.length > 0 && (
      <ul className="mt-2 text-sm text-ink-2 space-y-1">
        {f.candidates.map((c) => (
          <li key={c.club_label}>
            Dropping the {c.club_label} costs {yards(c.coverage_cost_yards)} of coverage
          </li>
        ))}
      </ul>
    )}
  </CardShell>
);

export const GapCard: React.FC<{ f: OverlapFinding | HoleFinding }> = ({ f }) => (
  <CardShell
    kicker={f.kind === 'hole' ? 'Hole' : 'Overlap'}
    accent={f.kind === 'hole' ? 'text-score-1' : 'text-ink-2'}
  >
    <p className="text-base text-ink">{f.summary}</p>
    <p className="text-sm text-ink-2 mt-1">
      {f.lower_label} → {f.upper_label} · {yards(f.gap_yards)} · tolerance ±{yards(f.tol_yards)}
    </p>
    {f.kind === 'hole' && f.closed_by_bend && (
      <p className="text-sm text-green-ink mt-1">
        A bend above already closes this — no purchase needed.
      </p>
    )}
  </CardShell>
);

export const FindingCard: React.FC<{ f: Finding }> = ({ f }) => {
  switch (f.kind) {
    case 'bend':
      return <BendCard f={f} />;
    case 'swap':
      return <SwapCard f={f} />;
    case 'slot':
      return <SlotCard f={f} />;
    case 'cut':
      return <CutCard f={f} />;
    case 'hole':
    case 'overlap':
      return <GapCard f={f} />;
    default:
      return null;
  }
};

export const BlockedBendCard: React.FC<{ b: BlockedBend }> = ({ b }) => (
  <div className="bg-score-2-bg border border-line rounded-brand px-4 py-3">
    <p className="text-sm font-semibold text-ink">
      {b.club_label}
      {b.degrees !== null &&
        b.degrees !== undefined &&
        ` · ${Math.abs(b.degrees).toFixed(1)}° held back`}
    </p>
    <p className="text-sm text-ink-2 mt-1">{b.reason}</p>
    {b.kind === 'estimated' && (
      <p className="text-sm text-cyan-ink mt-1">
        Confirm that carry on the Rack and this bend becomes advice.
      </p>
    )}
  </div>
);
